// BrandPickerSheet — chip → modal-sheet picker for the brand axis
// of an item's identity, per the CLAUDE.md item-reference visual
// pattern.
//
// When a `canonicalId` is supplied, this picker runs the same
// tiered resolver the AddItem wizard's BrandStep uses
// (brandsForCanonical + observations from pantry + curated
// expertise) and renders TWO pill rails:
//   1. "Curated for {canonical}" — user/family pantry brands +
//      explicit BRAND_EXPERTISE.canonicals[id] entries
//   2. "Common local brands for {hub}" — everything else
//      (subtype-claim coverage, USDA-derived, AI-suggested,
//      KNOWN_BRANDS hint), region-filtered.
// Per memory feedback_kitchen_inflow_pipelines.md: every surface
// that edits identity axes (Manual/Wizard/UPC/ShopMode/Receipt)
// must share this primitive so a UPC correction surfaces the
// same brands the wizard does.
//
// When canonicalId is null/undefined, falls back to the legacy
// flat `suggestions: string[]` shape (backward-compat for callers
// that haven't been updated yet).
//
// Caller wires the chip → open path; this component fires
// `onPick(brand)` and `onClose()`. Caller is responsible for
// persisting via onUpdate (and for firing rememberBarcodeCorrection
// when a UPC is on the row, per CLAUDE.md's WRITE cascade).

import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTheme, THEME_TRANSITION } from "./theme";
import { font } from "./tokens";
import {
  brandsForCanonical,
  BRAND_EXPERTISE,
  brandSlugify,
} from "../../data/brandExpertise";
import { findIngredient, findHub } from "../../data/ingredients";
import { useIngredientInfo } from "../../lib/useIngredientInfo";
import { useBrandObservations } from "../../lib/useBrandObservations";
import { useBrandsFromPantry } from "../../lib/useBrandsFromPantry";
import { useBrandIdentity } from "../../lib/useBrandIdentity";
import { useShoppingRegion } from "../../lib/useShoppingRegion";
import { useActiveStore } from "../../lib/useActiveStore";

export function BrandPickerSheet({
  initialValue = "",
  suggestions = [],
  canonicalId = null,
  onPick,
  onClose,
}) {
  const { theme } = useTheme();
  const [query, setQuery] = useState(initialValue);
  const inputRef = useRef(null);

  // Tier-aware resolver — same data sources as BrandStep.
  const { getInfo } = useIngredientInfo();
  const { observations: rawObservations } = useBrandObservations(canonicalId);
  const pantryBrands = useBrandsFromPantry(canonicalId);
  const observations = useMemo(() => {
    const out = [...(Array.isArray(rawObservations) ? rawObservations : [])];
    const seen = new Set(out.map((o) => (o.slug || (o.brand || "").toLowerCase().trim())));
    for (const p of pantryBrands || []) {
      const key = (p.brand || "").toLowerCase().trim();
      if (!key || seen.has(key)) continue;
      out.push(p);
      seen.add(key);
    }
    return out;
  }, [rawObservations, pantryBrands]);
  const { bySlug: brandIdentities } = useBrandIdentity();
  const [region] = useShoppingRegion();
  const [activeStore] = useActiveStore();

  const tieredSuggestions = useMemo(() => {
    if (!canonicalId) return [];
    const info = getInfo?.(canonicalId) || null;
    return brandsForCanonical(canonicalId, {
      limit: 16,
      observations,
      info,
      region,
      brandIdentities,
      // Mid-trip store context — boosts the store's house brands.
      store: activeStore,
    });
  }, [canonicalId, observations, getInfo, region, brandIdentities, activeStore]);

  const specialtySlugs = useMemo(() => {
    const set = new Set();
    for (const obs of observations || []) {
      const display = obs?.display || obs?.brand || null;
      const slug = obs?.slug || (display ? brandSlugify(display) : null);
      if (slug) set.add(slug);
    }
    if (canonicalId) {
      for (const [slug, rec] of Object.entries(BRAND_EXPERTISE)) {
        if (Number(rec?.canonicals?.[canonicalId] || 0) > 0) set.add(slug);
      }
    }
    return set;
  }, [observations, canonicalId]);

  const trimmed = query.trim();
  const trimmedLower = trimmed.toLowerCase();

  // When a canonical is provided, the typed-text filter is applied
  // across the tiered suggestions so the user can narrow rapidly
  // ("k" → Kerrygold). Without a canonical, fall back to the legacy
  // flat suggestion array.
  const visibleSuggestions = useMemo(() => {
    if (!canonicalId) return [];
    if (!trimmedLower) return tieredSuggestions;
    return tieredSuggestions.filter((s) =>
      String(s.display || "").toLowerCase().includes(trimmedLower),
    );
  }, [canonicalId, tieredSuggestions, trimmedLower]);

  const specialtySuggestions = useMemo(
    () => visibleSuggestions.filter((s) => specialtySlugs.has(s.slug)),
    [visibleSuggestions, specialtySlugs],
  );
  const commonSuggestions = useMemo(
    () => visibleSuggestions.filter((s) => !specialtySlugs.has(s.slug)),
    [visibleSuggestions, specialtySlugs],
  );

  // Legacy flat-list filter for callers without canonicalId.
  const filteredFlat = useMemo(() => {
    const arr = trimmedLower
      ? suggestions.filter((s) => s.toLowerCase().includes(trimmedLower))
      : suggestions.slice();
    return arr.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [suggestions, trimmedLower]);

  // True when the typed value would create a brand-new brand
  // (case-insensitive miss against any visible suggestion).
  const knownDisplays = useMemo(() => {
    if (canonicalId) {
      return new Set(visibleSuggestions.map((s) => String(s.display || "").toLowerCase()));
    }
    return new Set(suggestions.map((s) => s.toLowerCase()));
  }, [canonicalId, visibleSuggestions, suggestions]);
  const isNovel = trimmed && !knownDisplays.has(trimmedLower);

  // Auto-focus the input on mount so the user can start typing
  // immediately. Mobile keyboards open without a second tap.
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Esc closes; Enter commits the typed value.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const commit = (value) => {
    const v = (value ?? trimmed).trim();
    if (!v) return;
    onPick && onPick(v);
  };

  // Bucket label for the common section — derive from hub or
  // category, matching BrandStep's wording.
  const ing = canonicalId ? findIngredient(canonicalId) : null;
  const hub = ing?.parentId ? findHub(ing.parentId) : null;
  const bucket =
    (hub?.name && String(hub.name).toLowerCase()) ||
    (ing?.category && String(ing.category).toLowerCase()) ||
    "this item";
  const canonName = ing?.name || canonicalId || "this item";
  const commonLabel = region
    ? `Common local brands for ${bucket}`
    : `Common brands for ${bucket}`;

  // Pill renderer used by both tiers.
  const renderPill = (s) => (
    <button
      key={s.slug}
      type="button"
      className="mcm-focusable"
      onClick={(e) => { e.preventDefault(); commit(s.display); }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 12px",
        fontFamily: font.serif,
        fontStyle: "italic",
        fontSize: 14,
        fontWeight: 500,
        color: theme.color.ink,
        background: theme.color.glassFill,
        border: `1px solid ${theme.color.hairline}`,
        borderRadius: 999,
        cursor: "pointer",
      }}
    >
      {s.display}
    </button>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add brand"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        background: "rgba(20,12,4,0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <motion.div
        initial={{ y: 32, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 360, damping: 32 }}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "78vh",
          margin: "0 12px 24px",
          padding: "20px 18px 14px",
          borderRadius: 20,
          background: theme.color.glassFillHeavy,
          border: `1px solid ${theme.color.glassBorder}`,
          backdropFilter: "blur(12px) saturate(140%)",
          WebkitBackdropFilter: "blur(12px) saturate(140%)",
          boxShadow: "0 24px 60px rgba(20,12,4,0.40), 0 4px 16px rgba(20,12,4,0.20)",
          display: "flex", flexDirection: "column",
          ...THEME_TRANSITION,
        }}
      >
        <div style={{
          fontFamily: font.mono, fontSize: 11,
          letterSpacing: "0.16em", textTransform: "uppercase",
          color: theme.color.inkMuted,
          fontWeight: 600,
        }}>
          BRAND
        </div>
        <div style={{
          fontFamily: font.serif, fontStyle: "italic", fontWeight: 300,
          fontSize: 22, color: theme.color.ink,
          marginTop: 4, marginBottom: 14, letterSpacing: "-0.01em",
        }}>
          What brand?
        </div>
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          }}
          placeholder="Type a brand name…"
          style={{
            width: "100%",
            border: `1px solid ${theme.color.hairline}`,
            background: theme.color.glassFillHeavy,
            color: theme.color.ink,
            borderRadius: 10,
            padding: "10px 12px",
            fontFamily: font.sans, fontSize: 15,
            outline: "none",
            boxShadow: theme.shadow.inputInset,
            marginBottom: 12,
          }}
        />
        <div style={{ overflowY: "auto", margin: "0 -4px", padding: "0 4px" }}>
          {/* "Use this name" row — always at the top when the user
              has typed something not already in the suggestion set. */}
          {isNovel && (
            <button
              type="button"
              className="mcm-focusable"
              onClick={() => commit()}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                width: "100%",
                padding: "10px 12px",
                margin: "2px 0 10px",
                borderRadius: 12,
                border: `1px dashed ${theme.color.hairline}`,
                background: "transparent",
                cursor: "pointer", textAlign: "left",
                color: theme.color.ink,
              }}
            >
              <span style={{
                fontSize: 16, lineHeight: 1, color: theme.color.teal,
                fontWeight: 300,
              }}>+</span>
              <span style={{
                fontFamily: font.sans, fontSize: 14, fontWeight: 500,
              }}>
                Use “{trimmed}” as a new brand
              </span>
            </button>
          )}

          {/* TIERED PATH — fires when canonicalId is supplied. */}
          {canonicalId && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {specialtySuggestions.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{
                    fontFamily: font.mono, fontSize: 11,
                    letterSpacing: "0.16em", textTransform: "uppercase",
                    color: theme.color.ink,
                  }}>
                    {`Curated for ${canonName}`}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {specialtySuggestions.map(renderPill)}
                  </div>
                </div>
              )}

              {commonSuggestions.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{
                    fontFamily: font.mono, fontSize: 11,
                    letterSpacing: "0.16em", textTransform: "uppercase",
                    color: theme.color.inkMuted,
                  }}>
                    {commonLabel}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {commonSuggestions.map(renderPill)}
                  </div>
                </div>
              )}

              {!isNovel && visibleSuggestions.length === 0 && (
                <div style={{
                  padding: "16px 12px",
                  fontFamily: font.sans, fontSize: 13,
                  color: theme.color.inkMuted, textAlign: "center",
                }}>
                  {trimmed
                    ? `Nothing matches "${trimmed}".`
                    : "No brands yet — type one to start."}
                </div>
              )}
            </div>
          )}

          {/* LEGACY PATH — flat suggestions for callers without
              a canonicalId (rare; left in for backward compat). */}
          {!canonicalId && filteredFlat.map((name) => (
            <button
              key={name}
              type="button"
              className="mcm-focusable"
              onClick={() => commit(name)}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                width: "100%",
                padding: "10px 12px",
                margin: "2px 0",
                borderRadius: 12,
                border: "1px solid transparent",
                background: "transparent",
                cursor: "pointer", textAlign: "left",
                color: theme.color.ink,
                transition: "background 160ms ease",
              }}
            >
              <span style={{
                fontFamily: font.sans, fontSize: 15, fontWeight: 500,
                color: theme.color.ink,
              }}>
                {name}
              </span>
            </button>
          ))}
          {!canonicalId && !isNovel && filteredFlat.length === 0 && (
            <div style={{
              padding: "16px 12px",
              fontFamily: font.sans, fontSize: 13,
              color: theme.color.inkMuted, textAlign: "center",
            }}>
              {suggestions.length === 0
                ? "No brands yet — type one to start."
                : `Nothing matches "${trimmed}".`}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
